import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Star, Download, Eye, 
  CheckCircle, Clock, MapPin, Phone, Mail,
  Truck, Shield, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Orcamento {
  id: number;
  fornecedor: {
    id: number;
    nome: string;
    email: string;
    telefone: string;
    area_cobertura: string;
    avaliacao: number;
    especializacoes: string[];
    segmentos: string[];
    atende_brasil: boolean;
  };
  valor: number;
  condicao_peca: string;
  garantia: string;
  tempo_entrega: string;
  frete_incluso: boolean;
  observacoes: string;
  arquivo?: string;
  data_envio: string;
  escolhido?: boolean;
}

const OrcamentosPedido: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pedidoInfo, setPedidoInfo] = useState<any>(null);
  const [orcamentoEscolhido, setOrcamentoEscolhido] = useState<Orcamento | null>(null);

  useEffect(() => {
    loadOrcamentos();
  }, [id]);

  const loadOrcamentos = async () => {
    try {
      setIsLoading(true);
      console.log('Carregando orçamentos para pedido ID:', id);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Tentar buscar dados da API primeiro
      try {
        const response = await fetch(`/api/pedidos/${id}/orcamentos`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Orçamentos carregados da API:', data);
          
          // Mapear dados da API para o formato esperado
          const pedidoInfo = data.pedido ? {
            id: data.pedido.id,
            tipo_equipamento: data.pedido.tipo_equipamento || data.pedido.categoria || 'Equipamento',
            marca: data.pedido.marca || data.pedido.marca_texto || 'Marca',
            modelo: data.pedido.modelo || data.pedido.modelo_texto || 'Modelo',
            tipo_peca: data.pedido.tipo_peca || 'Peça',
            descricao: data.pedido.descricao || data.pedido.titulo || 'Descrição do pedido'
          } : {
            id: parseInt(id || '1'),
            tipo_equipamento: 'Equipamento',
            marca: 'Marca',
            modelo: 'Modelo',
            tipo_peca: 'Peça',
            descricao: 'Descrição do pedido'
          };
          
          // Mapear orçamentos da API
          const orcamentosMapeados = (data.orcamentos || []).map((orc: any) => ({
            id: orc.id,
            fornecedor: {
              id: orc.fornecedor_id,
              nome: orc.fornecedor_nome || 'Fornecedor',
              email: orc.fornecedor_email || 'contato@fornecedor.com',
              telefone: orc.fornecedor_telefone || '(11) 00000-0000',
              area_cobertura: 'SP, RJ, MG',
              avaliacao: 4.5,
              especializacoes: ['Peças de Reposição', 'Peças de Manutenção'],
              segmentos: ['Mineração', 'Construção Civil'],
              atende_brasil: true
            },
            valor: orc.valor_final || orc.valor_total || orc.valor || 0,
            condicao_peca: orc.condicao_peca || 'Nova',
            garantia: orc.garantia_meses ? `${orc.garantia_meses} meses` : '6 meses',
            tempo_entrega: orc.prazo_entrega || '5-7 dias úteis',
            frete_incluso: orc.frete_incluso || false,
            observacoes: orc.observacoes || 'Orçamento enviado via plataforma.',
            data_envio: orc.created_at || new Date().toISOString().split('T')[0],
            arquivo: orc.documentos_tecnicos ? `proposta_${orc.id}.pdf` : undefined
          }));
          
          setPedidoInfo(pedidoInfo);
          setOrcamentos(orcamentosMapeados);
          return;
        } else if (response.status === 404) {
          console.log('Orçamentos não encontrados na API, usando dados mockados');
        }
      } catch (apiError) {
        console.log('Erro ao buscar da API, usando dados mockados:', apiError);
      }

      // Fallback para dados mockados se a API não estiver disponível
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const pedidoId = parseInt(id || '1');
      
      // Usar o mesmo mapeamento da página de detalhes
      const orcamentosMapping: { [key: number]: number } = {
        4: 1,  // ID 4 tem 1 orçamento
        7: 2,  // ID 7 tem 2 orçamentos
        10: 1, // ID 10 tem 1 orçamento
        13: 2, // ID 13 tem 2 orçamentos
        16: 1, // ID 16 tem 1 orçamento
        19: 2, // ID 19 tem 2 orçamentos
        22: 1, // ID 22 tem 1 orçamento
        25: 2, // ID 25 tem 2 orçamentos
        28: 1, // ID 28 tem 1 orçamento
        31: 2  // ID 31 tem 2 orçamentos
      };
      
      const orcamentosCount = orcamentosMapping[pedidoId] !== undefined ? orcamentosMapping[pedidoId] : pedidoId % 3;
      
      // Criar dados do pedido dinamicamente
      const mockPedido = {
        id: pedidoId,
        tipo_equipamento: pedidoId % 3 === 0 ? 'Trator' : pedidoId % 2 === 0 ? 'Pá Carregadeira' : 'Escavadeira',
        marca: pedidoId % 3 === 0 ? 'John Deere' : pedidoId % 2 === 0 ? 'Komatsu' : 'Caterpillar',
        modelo: pedidoId % 3 === 0 ? '750J' : pedidoId % 2 === 0 ? 'WA380' : '320D',
        tipo_peca: pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema Hidráulico' : 'Motor',
        descricao: `${pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema hidráulico' : 'Motor'} completo para ${pedidoId % 3 === 0 ? 'trator' : pedidoId % 2 === 0 ? 'pá carregadeira' : 'escavadeira'} ${pedidoId % 3 === 0 ? 'John Deere 750J' : pedidoId % 2 === 0 ? 'Komatsu WA380' : 'Caterpillar 320D'}`
      };
      
      setPedidoInfo(mockPedido);
      console.log('Informações do pedido carregadas:', mockPedido);
      
      // Criar orçamentos dinamicamente baseados no ID do pedido
      const mockOrcamentos: Orcamento[] = [];
      
      if (orcamentosCount > 0) {
        const fornecedores = [
          {
            id: 1,
            nome: 'Peças Express Ltda',
            email: 'contato@pecasexpress.com',
            telefone: '(11) 88888-8888',
            area_cobertura: 'SP, RJ, MG',
            avaliacao: 4.8,
            especializacoes: ['Peças de Reposição', 'Peças de Manutenção'],
            segmentos: ['Mineração', 'Construção Civil'],
            atende_brasil: true
          },
          {
            id: 2,
            nome: 'Mecânica Industrial Ltda',
            email: 'vendas@mecanicaindustrial.com',
            telefone: '(11) 77777-7777',
            area_cobertura: 'SP, PR, SC',
            avaliacao: 4.5,
            especializacoes: ['Peças de Reposição', 'Peças de Manutenção'],
            segmentos: ['Mineração', 'Construção Civil'],
            atende_brasil: true
          },
          {
            id: 3,
            nome: 'Auto Peças Santos',
            email: 'contato@autopecassantos.com',
            telefone: '(13) 66666-6666',
            area_cobertura: 'SP, RJ',
            avaliacao: 4.2,
            especializacoes: ['Peças de Reposição', 'Peças de Manutenção'],
            segmentos: ['Mineração', 'Construção Civil'],
            atende_brasil: true
          }
        ];
        
        for (let i = 0; i < orcamentosCount; i++) {
          const fornecedor = fornecedores[i % fornecedores.length];
          mockOrcamentos.push({
            id: pedidoId * 10 + i + 1,
            fornecedor: fornecedor,
            valor: 12000 + (pedidoId * 100) + (i * 500),
            condicao_peca: i === 0 ? 'Nova' : 'Recondicionada',
            garantia: i === 0 ? '12 meses' : '6 meses',
            tempo_entrega: i === 0 ? '5-7 dias úteis' : '3-5 dias úteis',
            frete_incluso: i === 0,
            observacoes: `${pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema hidráulico' : 'Motor'} ${i === 0 ? 'original' : 'recondicionado'} com garantia ${i === 0 ? 'de fábrica' : 'garantida'}. ${i === 0 ? 'Entrega em todo o Brasil' : 'Testado e funcionando perfeitamente'}.`,
            data_envio: new Date(Date.now() - (pedidoId * 24 * 60 * 60 * 1000) - (i * 2 * 60 * 60 * 1000)).toISOString().split('T')[0],
            arquivo: `proposta_${fornecedor.nome.toLowerCase().replace(/\s+/g, '')}_${pedidoId.toString().padStart(3, '0')}.pdf`
          });
        }
      }
      
      setOrcamentos(mockOrcamentos);
      console.log('Orçamentos carregados (mockados):', mockOrcamentos);
      
    } catch (error) {
      console.error('Erro ao carregar orçamentos:', error);
      toast.error('Erro ao carregar orçamentos');
      navigate('/cliente/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscolherFornecedor = (orcamento: Orcamento) => {
    if (orcamentoEscolhido && orcamentoEscolhido.id !== orcamento.id) {
      toast.error('Você já escolheu um orçamento. Só é possível escolher um orçamento por pedido.');
      return;
    }
    
    setOrcamentoEscolhido(orcamento);
    toast.success(`Orçamento escolhido com sucesso!`);
    
    // Atualizar o status do orçamento
    setOrcamentos(prev => prev.map(o => ({
      ...o,
      escolhido: o.id === orcamento.id
    })));
  };

  const handleVerDetalhes = (orcamento: Orcamento) => {
    // Se o orçamento foi escolhido, mostrar detalhes completos
    if (orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id) {
      navigate(`/cliente/fornecedor/${orcamento.fornecedor.id}`);
    } else {
      // Mostrar modal com detalhes limitados
      toast('Para ver detalhes completos, escolha este orçamento primeiro.');
    }
  };

  const handleDownloadProposta = (orcamento: Orcamento) => {
    if (!orcamento.arquivo) {
      toast.error('Arquivo não disponível');
      return;
    }

    // Simular download do arquivo
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,JVBERi0xLjQKJcOkw7zDtsO...`; // Base64 simulado
    link.download = orcamento.arquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Proposta ${orcamento.arquivo} baixada com sucesso!`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-4 h-4 ${
            i <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando orçamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-900">PeçaJá</span>
            </div>

            <button
              onClick={() => navigate('/cliente/dashboard')}
              className="btn btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="breadcrumb-item"
          >
            Dashboard
          </button>
          <span className="breadcrumb-separator">/</span>
          <button
            onClick={() => navigate(`/cliente/pedidos/${id}`)}
            className="breadcrumb-item"
          >
            Pedido #{id}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Orçamentos</span>
        </div>

        {/* Pedido Info */}
        {pedidoInfo && (
          <div className="card mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {pedidoInfo.tipo_equipamento} {pedidoInfo.marca} {pedidoInfo.modelo}
                </h1>
                <p className="text-lg text-gray-600">
                  <span className="font-medium">Peça:</span> {pedidoInfo.tipo_peca}
                </p>
                <p className="text-gray-600 mt-2">{pedidoInfo.descricao}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Orçamentos Recebidos</p>
                <p className="text-2xl font-bold text-green-600">{orcamentos.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Orçamentos */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Orçamentos Disponíveis
            </h2>
            <p className="text-sm text-gray-500">
              {orcamentos.length} proposta{orcamentos.length !== 1 ? 's' : ''} recebida{orcamentos.length !== 1 ? 's' : ''}
            </p>
          </div>

          {orcamentos.map((orcamento, index) => (
            <div key={orcamento.id} className="card">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Fornecedor Info - Anônimo até escolhido */}
                <div className="lg:col-span-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id 
                          ? orcamento.fornecedor.nome 
                          : `Fornecedor ${orcamento.id}`
                        }
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex items-center">
                          {renderStars(orcamento.fornecedor.avaliacao)}
                        </div>
                        <span className="text-sm text-gray-500">
                          {orcamento.fornecedor.avaliacao}/5
                        </span>
                      </div>
                    </div>
                    {index === 0 && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                        Mais Rápido
                      </span>
                    )}
                    {orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        Escolhido
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>
                        {orcamento.fornecedor.atende_brasil ? 'Todo Brasil' : orcamento.fornecedor.area_cobertura}
                      </span>
                    </div>
                    
                    {/* Informações anônimas */}
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Especializações:</p>
                      <div className="flex flex-wrap gap-1">
                        {orcamento.fornecedor.especializacoes.map((esp, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {esp}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">Segmentos:</p>
                      <div className="flex flex-wrap gap-1">
                        {orcamento.fornecedor.segmentos.map((seg, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {seg}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Informações de contato só aparecem se escolhido */}
                    {orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id && (
                      <>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{orcamento.fornecedor.telefone}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{orcamento.fornecedor.email}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Orçamento Details */}
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Valor</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(orcamento.valor)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Condição da Peça</p>
                      <p className="font-medium text-gray-900">{orcamento.condicao_peca}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Garantia</p>
                      <p className="font-medium text-gray-900">{orcamento.garantia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tempo de Entrega</p>
                      <p className="font-medium text-gray-900">{orcamento.tempo_entrega}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-2">
                      {orcamento.frete_incluso ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Clock className="w-4 h-4 text-gray-400" />
                      )}
                      <span className="text-sm text-gray-600">
                        Frete {orcamento.frete_incluso ? 'Incluso' : 'Não Incluso'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600">Garantia</span>
                    </div>
                  </div>

                  {orcamento.observacoes && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">Observações</p>
                      <p className="text-sm text-gray-600">{orcamento.observacoes}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Enviado em: {new Date(orcamento.data_envio).toLocaleDateString('pt-BR')}
                    </div>
                    <div className="flex space-x-2">
                      {orcamento.arquivo && (
                        <button 
                          onClick={() => handleDownloadProposta(orcamento)}
                          className="btn btn-outline"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Baixar Proposta
                        </button>
                      )}
                      <button
                        onClick={() => handleVerDetalhes(orcamento)}
                        className="btn btn-outline"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Ver Detalhes
                      </button>
                      <button
                        onClick={() => handleEscolherFornecedor(orcamento)}
                        className={`btn ${orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id ? 'btn-secondary' : 'btn-primary'}`}
                        disabled={!!(orcamentoEscolhido && orcamentoEscolhido.id !== orcamento.id)}
                      >
                        {orcamentoEscolhido && orcamentoEscolhido.id === orcamento.id ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Escolhido
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Escolher
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {orcamentos.length === 0 && (
          <div className="card text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum orçamento recebido
            </h3>
            <p className="text-gray-600 mb-4">
              Ainda não recebemos orçamentos para este pedido. Aguarde os fornecedores entrarem em contato.
            </p>
            <button
              onClick={() => navigate('/cliente/dashboard')}
              className="btn btn-primary"
            >
              Voltar ao Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrcamentosPedido; 